import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import {
  getMyCandidateProfile,
  saveCandidateProfile,
  type CandidateProfile,
} from "@/lib/cv-parse.functions";
import { Button } from "@/components/ui/button";

export const Route = createFileRoute("/_authenticated/bartender/profile-review")({
  head: () => ({
    meta: [
      { title: "Review & Confirm Profile — The Hospitality Reserve" },
      {
        name: "description",
        content:
          "Check the bartender profile we built from your CV — bio, skills, experience and work history — then continue to identity verification.",
      },
    ],
  }),
  component: ProfileReviewPage,
});

function ProfileReviewPage() {
  const navigate = useNavigate();
  const load = useServerFn(getMyCandidateProfile);
  const save = useServerFn(saveCandidateProfile);

  const [profile, setProfile] = useState<CandidateProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [bio, setBio] = useState("");
  const [skills, setSkills] = useState<string[]>([]);
  const [skillDraft, setSkillDraft] = useState("");
  const [rows, setRows] = useState<CandidateProfile["work_experiences"]>([]);

  useEffect(() => {
    (async () => {
      const r = await load();
      if ("error" in r) { toast.error(r.error); navigate({ to: "/dashboard", replace: true }); return; }
      if (r.onboarding_status === "rejected") { navigate({ to: "/bartender/rejected", replace: true }); return; }
      if (!r.cv_parsed_at) { navigate({ to: "/bartender/application", replace: true }); return; }
      setProfile(r);
      setBio(r.ai_summary ?? "");
      setSkills(r.skills ?? []);
      setRows(r.work_experiences);
      setLoading(false);
    })();
  }, []);

  const updateRow = (id: string, patch: Partial<CandidateProfile["work_experiences"][number]>) =>
    setRows((prev) => prev.map((r) => (r.id === id ? { ...r, ...patch } : r)));

  const onSave = async (next: boolean) => {
    setSaving(true);
    try {
      const r = await save({
        data: {
          ai_summary: bio.slice(0, 600),
          skills: skills.slice(0, 12),
          work_experiences: rows.map((w) => ({
            id: w.id,
            role: w.role ?? "",
            venue_name: w.venue_name ?? "",
            dates: w.dates ?? "",
            description: w.description ?? "",
          })),
        },
      });
      if ("error" in r) throw new Error(r.error);
      toast.success("Profile saved.");
      if (next) navigate({ to: "/bartender" });
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Could not save profile.");
    } finally {
      setSaving(false);
    }
  };

  if (loading || !profile) {
    return (
      <div className="min-h-screen bg-background text-foreground flex items-center justify-center">
        <p className="tracking-wide-luxury text-muted-foreground">Loading…</p>
      </div>
    );
  }

  const months = profile.total_months_experience ?? 0;
  const years = Math.floor(months / 12);
  const remainder = months % 12;

  return (
    <div className="min-h-screen bg-background text-foreground">
      <header className="px-6 py-5 border-b border-border">
        <p className="tracking-wide-luxury text-accent text-xs">The Hospitality Reserve</p>
        <h1 className="font-display text-lg leading-tight">Review &amp; Confirm Profile</h1>
      </header>

      <main className="mx-auto w-full max-w-2xl px-5 py-10 space-y-6">
        <section className="rounded-2xl bg-white p-6 shadow-sm">
          <div className="flex flex-wrap items-center gap-3">
            <span className="rounded-full bg-emerald-500/10 px-3 py-1 text-xs font-medium text-emerald-700">
              Experience verified
            </span>
            {profile.has_personal_license && (
              <span className="rounded-full bg-accent/10 px-3 py-1 text-xs font-medium text-accent">
                Personal licence
              </span>
            )}
            {profile.candidate_score != null && (
              <span className="rounded-full bg-foreground/5 px-3 py-1 text-xs font-medium">
                Score {profile.candidate_score}/5
              </span>
            )}
          </div>
          <p className="mt-4 font-display text-2xl">
            {years > 0 ? `${years} yr${years > 1 ? "s" : ""}${remainder ? ` ${remainder} mo` : ""}` : `${months} months`}
          </p>
          <p className="text-sm text-foreground/60">combined bar &amp; hospitality experience</p>
        </section>

        <section className="rounded-2xl bg-white p-6 shadow-sm">
          <h2 className="font-display text-lg">Your bio</h2>
          <textarea
            value={bio}
            maxLength={600}
            rows={4}
            onChange={(e) => setBio(e.target.value)}
            className="mt-3 w-full rounded-xl border border-foreground/20 px-3 py-2 text-sm focus:border-foreground focus:outline-none focus:ring-2 focus:ring-accent/30"
          />
        </section>

        <section className="rounded-2xl bg-white p-6 shadow-sm">
          <h2 className="font-display text-lg">Skills</h2>
          <div className="mt-3 flex flex-wrap gap-2">
            {skills.map((s) => (
              <button
                key={s}
                type="button"
                onClick={() => setSkills((prev) => prev.filter((x) => x !== s))}
                className="rounded-full bg-foreground/5 px-3 py-1 text-xs hover:bg-destructive/10"
              >
                {s} <span aria-hidden>×</span>
                <span className="sr-only">Remove {s}</span>
              </button>
            ))}
            {skills.length === 0 && <p className="text-sm text-foreground/50">No skills yet — add a few.</p>}
          </div>
          <div className="mt-4 flex gap-2">
            <input
              value={skillDraft}
              maxLength={40}
              placeholder="Add a skill"
              onChange={(e) => setSkillDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  const v = skillDraft.trim();
                  if (v && skills.length < 12 && !skills.includes(v)) setSkills([...skills, v]);
                  setSkillDraft("");
                }
              }}
              className="flex-1 rounded-xl border border-foreground/20 px-3 py-2 text-sm focus:border-foreground focus:outline-none focus:ring-2 focus:ring-accent/30"
            />
          </div>
        </section>

        <section className="rounded-2xl bg-white p-6 shadow-sm">
          <h2 className="font-display text-lg">Work history</h2>
          <div className="mt-4 space-y-4">
            {rows.map((w) => (
              <div key={w.id} className="rounded-xl border border-foreground/10 p-4">
                <div className="grid gap-3 sm:grid-cols-2">
                  <input
                    aria-label="Role"
                    value={w.role ?? ""}
                    onChange={(e) => updateRow(w.id, { role: e.target.value })}
                    className="rounded-xl border border-foreground/20 px-3 py-2 text-sm focus:border-foreground focus:outline-none"
                  />
                  <input
                    aria-label="Venue"
                    value={w.venue_name ?? ""}
                    onChange={(e) => updateRow(w.id, { venue_name: e.target.value })}
                    className="rounded-xl border border-foreground/20 px-3 py-2 text-sm focus:border-foreground focus:outline-none"
                  />
                </div>
                <input
                  aria-label="Dates"
                  value={w.dates ?? ""}
                  onChange={(e) => updateRow(w.id, { dates: e.target.value })}
                  className="mt-3 w-full rounded-xl border border-foreground/20 px-3 py-2 text-sm focus:border-foreground focus:outline-none"
                />
                <textarea
                  aria-label="Description"
                  rows={2}
                  value={w.description ?? ""}
                  onChange={(e) => updateRow(w.id, { description: e.target.value })}
                  className="mt-3 w-full rounded-xl border border-foreground/20 px-3 py-2 text-sm focus:border-foreground focus:outline-none"
                />
                {w.duration_months != null && (
                  <p className="mt-2 text-xs text-foreground/50">{w.duration_months} months</p>
                )}
              </div>
            ))}
            {rows.length === 0 && (
              <p className="text-sm text-foreground/50">No roles were detected on your CV.</p>
            )}
          </div>
        </section>

        <div className="flex flex-col gap-3 sm:flex-row">
          <Button
            type="button"
            variant="outline"
            disabled={saving}
            onClick={() => void onSave(false)}
            className="flex-1 rounded-xl py-6 tracking-wide-luxury"
          >
            {saving ? "Saving…" : "Save changes"}
          </Button>
          <Button
            type="button"
            disabled={saving}
            onClick={() => void onSave(true)}
            className="flex-1 rounded-xl bg-foreground text-background hover:bg-foreground/90 py-6 tracking-wide-luxury"
          >
            Continue to identity verification
          </Button>
        </div>
      </main>
    </div>
  );
}
